import { useEffect, useRef } from 'react';
import { useUnit } from 'effector-react';
import { button, useControls } from 'leva';
import type { Schema } from 'leva/dist/declarations/src/types/public.js';
import { $splatDevHandles } from '../../state/splatDevTools';

type ChangeContext = { initial?: boolean };
type RoutesSet = (values: { Route?: string; 'Edit Routes'?: boolean }) => void;

/**
 * Flight editor for splat scenes (`?flight`): one Leva folder, `Routes` — the
 * SDK route editor and the preloader-harness player, both owned by the scene
 * (`devEditorTools.ts`). Renders null. Empty — so hidden — on a scene that
 * carries no routes.
 */
export const FlightEditorPanel = () => {
  const tools = useUnit($splatDevHandles)?.editorTools ?? null;
  const selectedRef = useRef<string | null>(null);
  const setRef = useRef<RoutesSet | null>(null);

  const [, set] = useControls(
    'Routes',
    (): Schema => {
      const ids = tools?.routeIds() ?? [];
      if (!tools || ids.length === 0) return {};
      return {
        Route: {
          options: ids,
          value: ids[0],
          onChange: (id: string) => {
            selectedRef.current = ids.includes(id) ? id : ids[0];
            tools.selectRoute(selectedRef.current);
          },
        },
        'Edit Routes': {
          value: false,
          hint: 'Редактор маршрутов SDK. Не включается, пока летит прелоадер.',
          onChange: (on: boolean, _path: string, ctx: ChangeContext) => {
            if (ctx.initial) return;
            void tools.setEditing(on).then((accepted) => {
              if (!accepted) setRef.current?.({ 'Edit Routes': false });
            });
          },
        },
        Play: button(() => {
          if (selectedRef.current) tools.playRoute(selectedRef.current);
        }),
        Stop: button(() => tools.stopRoute()),
        'Copy Routes': button(() => tools.copyRoutes()),
      };
    },
    [tools],
  );

  // Leva keeps a value across a schema rebuild, so a new scene would inherit
  // the last scene's toggle while its own editor is off.
  useEffect(() => {
    setRef.current = set as RoutesSet;
    const first = tools?.routeIds()[0];
    if (first) set({ Route: first, 'Edit Routes': false });
  }, [set, tools]);

  return null;
};
